import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PaymentMobileReturnPage.css';

/**
 * HTTPS intermediate landing for Stripe hosted Checkout → mobile app.
 *
 * Why this page exists:
 *   Mobile Stripe Checkout cannot reliably redirect directly to a custom URL
 *   scheme (`ria://payment/...`) across every browser/OS combo. iOS Safari
 *   in particular silently drops a `window.location.href = 'ria://...'`
 *   that fires on page load without a user gesture (pop-up blocker rule
 *   introduced around iOS 15 and tightened since). Stripe is happy with
 *   HTTPS success_url, so backend points success_url / cancel_url at this
 *   page, and this page hops the user over to `ria://` with the most
 *   reliable technique for each platform.
 *
 * Redirect strategy (fallbacks all run — first one that succeeds wins):
 *   1. Programmatic click on a hidden <a href="ria://..." rel="noopener">.
 *      Most reliable on iOS Safari ≥ 15 because the browser treats it as
 *      a user-initiated navigation hop.
 *   2. window.location.replace(deepLink). Works on Android Chrome and
 *      older iOS.
 *   3. window.location.href = deepLink. Last-ditch; sometimes blocked
 *      when the first two fail.
 *   4. Visible "Open Rialty" button — always present as a user-gesture
 *      fallback for the browser that refuses all three above.
 */
const PaymentMobileReturnPage = () => {
  const { outcome, deepLink } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const rawOutcome = params.get('outcome');
    const pathname = window.location.pathname.toLowerCase();
    const resolved = rawOutcome === 'canceled' || pathname.includes('cancel')
      ? 'canceled'
      : 'complete';
    const sessionId = params.get('session_id');
    const query = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
    return {
      outcome: resolved,
      deepLink: `ria://payment/${resolved}${query}`,
    };
  }, []);

  const [autoNavTried, setAutoNavTried] = useState(false);
  const hiddenLinkRef = useRef(null);

  useEffect(() => {
    // Fire all three techniques quickly so whichever one the current
    // browser honors takes effect. The flag prompts the hint UI if the
    // page is still here a moment later.
    const t = setTimeout(() => {
      try {
        if (hiddenLinkRef.current && typeof hiddenLinkRef.current.click === 'function') {
          hiddenLinkRef.current.click();
        }
      } catch (e) { /* ignore — fall through */ }
      try {
        window.location.replace(deepLink);
      } catch (e) { /* ignore — fall through */ }
      try {
        window.location.href = deepLink;
      } catch (e) { /* ignore — manual button is the fallback */ }
      setAutoNavTried(true);
    }, 150);
    return () => clearTimeout(t);
  }, [deepLink]);

  const isCanceled = outcome === 'canceled';

  return (
    <div className="pmr-root">
      <div className="pmr-card">
        <div className={`pmr-badge ${isCanceled ? 'pmr-badge--warning' : 'pmr-badge--success'}`}>
          {isCanceled ? '!' : '✓'}
        </div>
        <h1 className="pmr-title">
          {isCanceled ? 'Checkout canceled' : 'Payment received'}
        </h1>
        <p className="pmr-body">
          {isCanceled
            ? 'No payment was made. Returning you to the Rialty app…'
            : 'Thanks — we’re returning you to the Rialty app to confirm.'}
        </p>
        <a className="pmr-button" href={deepLink}>
          Open Rialty
        </a>
        {autoNavTried && (
          <p className="pmr-hint">
            If the app doesn’t open automatically, tap <strong>Open Rialty</strong> above.
          </p>
        )}
        {/* Hidden <a> used by the first auto-redirect technique. iOS Safari
            treats a programmatic click on a real <a> element as a user-
            initiated nav, so custom URL schemes trigger reliably when
            window.location is blocked. */}
        <a
          ref={hiddenLinkRef}
          href={deepLink}
          style={{ display: 'none' }}
          aria-hidden="true"
          tabIndex={-1}
        >
          hidden
        </a>
      </div>
    </div>
  );
};

export default PaymentMobileReturnPage;
